import { useEffect, useState } from "react";
import { Row } from "react-bootstrap";
import swal from "sweetalert";
import { isEmailIdRegexIsValid, isMobileNumberValid } from "../../utils/helper";

const MasterMerchantForm = ({
  mode = "create",
  initialData = {},
  privilegesData = [],
  selectedPrivileges = [],
  setSelectedPrivileges,
  onSubmit,
  loading,
}) => {
  const [formData, setFormData] = useState({
    AggregatorName: "",
    emailId: "",
    MobileNumber: "",
    Prifix: "",
    password: "",
  });

  /* ======================
     INIT DATA (EDIT MODE)
  ======================= */
  useEffect(() => {
    if (mode === "edit" && initialData) {
      setFormData({
        AggregatorName: initialData.name || "",
        emailId: initialData.email || "",
        MobileNumber: initialData.mobile || "",
        Prifix: initialData.midprifix || "",
        password: initialData.password || "",
      });
    }
  }, [mode, initialData]);

  /* ======================
     INPUT HANDLER
  ======================= */
  const handleChange = (e) => {
    let { name, value } = e.target;

    if (name === "Prifix") {
      value = value
        .slice(0, 2)
        .toUpperCase()
        .replace(/[^A-Z]/g, "");
    } else if (name === "MobileNumber") {
      value = value.replace(/\D/g, "");
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* ======================
     PRIVILEGE HELPERS
  ======================= */
  const isSubSelected = (id) => selectedPrivileges.includes(id + "");

  const isHeadSelected = (subTypes) => {
    if (!subTypes || subTypes.length === 0) return false;

    return subTypes.every((s) =>
      selectedPrivileges.includes(s.adminPrivilegeSubTypeId + "")
    );
  };

  /* ======================
     PRIVILEGE TOGGLES
  ======================= */
  const toggleSubPrivilege = (subId) => {
    setSelectedPrivileges((prev) =>
      prev.includes(subId + "")
        ? prev.filter((id) => id !== subId + "")
        : [...prev, subId + ""]
    );
  };

  const toggleHeadPrivilege = (head) => {
    if (!head.subTypes || head.subTypes.length === 0) return;

    const subIds = head.subTypes.map((s) => s.adminPrivilegeSubTypeId + "");

    const allSelected = isHeadSelected(head.subTypes);

    if (allSelected) {
      setSelectedPrivileges((prev) =>
        prev.filter((id) => !subIds.includes(id))
      );
    } else {
      setSelectedPrivileges((prev) => [...new Set([...prev, ...subIds])]);
    }
  };

  /* ======================
     VALIDATION
  ======================= */
  const validate = () => {
    if (!formData.AggregatorName.trim()) {
      swal("Error", "Please Enter Master Merchant Name", "error");
      return false;
    }

    if (!isMobileNumberValid(formData.MobileNumber)) {
      swal("Error", "Please Enter Valid Mobile Number", "error");
      return false;
    }

    if (!formData.Prifix.trim()) {
      swal("Error", "Please Enter Prefix", "error");
      return false;
    }

    if (!formData.emailId.trim()) {
      swal("Error", "Please Enter Email ID", "error");
      return false;
    }

    if (!isEmailIdRegexIsValid(formData.emailId)) {
      swal("Error", "Please Enter Valid Email ID", "error");
      return false;
    }

    if (!formData.password.trim()) {
      swal("Error", "Please Enter Password", "error");
      return false;
    }

    if (!selectedPrivileges.length) {
      swal("Error", "Please select at least one privilege", "error");
      return false;
    }

    return true;
  };

  /* ======================
     SUBMIT
  ======================= */
  const handleSubmit = () => {
    if (!validate()) return;

    const payload = {
      name: formData.AggregatorName.trim(),
      email: formData.emailId.trim(),
      mobile: formData.MobileNumber.trim(),
      midprifix: formData.Prifix.trim(),
      password: formData.password.trim(),
      adminPrivilege: selectedPrivileges.map(Number),
    };

    onSubmit(payload);
  };

  return (
    <>
      {/* ================= FORM ================= */}
      <div className="row">
        <div className="col-lg-6 mb-2">
          <label className="fs-4">Master Merchant Name</label>
          <input
            className="form-control"
            name="AggregatorName"
            value={formData.AggregatorName}
            onChange={handleChange}
          />
        </div>

        <div className="col-lg-6 mb-2">
          <label className="fs-4">Email</label>
          <input
            className="form-control"
            name="emailId"
            value={formData.emailId}
            onChange={handleChange}
          />
        </div>

        <div className="col-lg-6 mb-2">
          <label className="fs-4">Mobile Number</label>
          <input
            className="form-control"
            maxLength={10}
            name="MobileNumber"
            value={formData.MobileNumber}
            onChange={handleChange}
          />
        </div>

        <div className="col-lg-6 mb-2">
          <label className="fs-4">Prefix</label>
          <input
            className="form-control"
            name="Prifix"
            value={formData.Prifix}
            onChange={handleChange}
          />
        </div>

        <div className="col-lg-6 mb-2">
          <label className="fs-4">Password</label>
          <input
            className="form-control"
            name="password"
            value={formData.password}
            onChange={handleChange}
          />
        </div>
      </div>

      {/* ================= PRIVILEGES ================= */}
      <div className="pt-4">
        <h4 className="fs-2">Privileges</h4>
      </div>

      <Row className="mt-3">
        <div className="d-flex flex-column gap-4">
          {privilegesData
            ?.filter((p) => p.privilegeName !== "Master Merchant Management")
            .map((head) => {
              const headChecked = isHeadSelected(head?.subTypes);

              return (
                <div key={head.adminPrivilegeId}>
                  {/* HEAD */}
                  <div className="d-flex align-items-center gap-2">
                    <div className="checkbox-wrapper-31">
                      <input
                        type="checkbox"
                        checked={headChecked}
                        onChange={() => toggleHeadPrivilege(head)}
                      />
                      <svg viewBox="0 0 35.6 35.6">
                        <circle
                          className="background"
                          cx="17.8"
                          cy="17.8"
                          r="17.8"
                        />
                        <circle
                          className="stroke"
                          cx="17.8"
                          cy="17.8"
                          r="14.37"
                        />
                        <polyline
                          className="check"
                          points="11.78 18.12 15.55 22.23 25.17 12.87"
                        />
                      </svg>
                    </div>
                    <h1 className="pt-2">{head.privilegeName}</h1>
                  </div>

                  {/* SUB TYPES */}
                  <div className="row fs-4 mt-2 text-dark">
                    {head?.subTypes.map((sub) => (
                      <div
                        key={sub.adminPrivilegeSubTypeId}
                        className="checkbox-wrapper-27 col-lg-4 col-md-6 col-sm-12 d-flex"
                      >
                        <label className="checkbox">
                          <input
                            type="checkbox"
                            checked={isSubSelected(sub.adminPrivilegeSubTypeId)}
                            onChange={() =>
                              toggleSubPrivilege(sub.adminPrivilegeSubTypeId)
                            }
                          />
                          <span className="checkbox__icon"></span>
                          {sub.subTypePrivilegeName}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
        </div>
      </Row>

      {/* ================= ACTION ================= */}
      <div className="d-flex justify-content-end mt-4">
        <button
          className="badge badge-rounded badge-primary px-4 py-3"
          onClick={handleSubmit}
          disabled={loading}
        >
          {mode === "edit" ? "Update" : "Submit"}
        </button>
      </div>
    </>
  );
};

export default MasterMerchantForm;
